/**
 * EncryptedHlsLoader
 * 
 * Custom HLS.js loader that decrypts video segments on-the-fly.
 * Integrates with hls.js to provide transparent decryption during playback.
 * 
 * Key features:
 * - Fetches encrypted video chunks from CDN
 * - Decrypts chunks using AES-128-CBC before passing to player
 * - Handles CDN authentication with HMAC tokens
 * - Transparent to the video player (decryption happens in loader)
 */

import type { Loader, LoaderStats, LoaderConfiguration, LoaderCallbacks, LoaderContext, HlsConfig } from 'hls.js';
import { videoEncryptionService } from '@/domain/services/video/videoEncryptionService';
import type { VideoDecryptionKeys } from '@/domain/entities/video/videoDecryptionEntity';
import { videoLogger } from '@/domain/services/analytics/videoLogger';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface EncryptedLoaderConfig {
  videoId: string;
  decryptionKeys: VideoDecryptionKeys;
}

/**
 * Custom loader for HLS.js that decrypts video segments
 */
export class EncryptedHlsLoader implements Loader<LoaderContext> {
  context: LoaderContext | null = null;
  stats: LoaderStats;
  private abortController: AbortController | null = null;

  private encryptedLoaderConfig: EncryptedLoaderConfig;

  constructor(encryptedConfig: EncryptedLoaderConfig, _config?: LoaderConfiguration) {
    this.encryptedLoaderConfig = encryptedConfig;
    this.stats = {
      aborted: false,
      loaded: 0,
      retry: 0,
      total: 0,
      chunkCount: 0,
      bwEstimate: 0,
      loading: {
        start: 0,
        first: 0,
        end: 0
      },
      parsing: {
        start: 0,
        end: 0
      },
      buffering: {
        start: 0,
        first: 0,
        end: 0
      }
    };
  }

  /**
   * Destroy the loader and cleanup
   */
  destroy(): void {
    this.abort();
    this.context = null;
  }

  /**
   * Abort the current request
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.stats.aborted = true;
  }

  /**
   * Load a video segment
   */
  load(
    context: LoaderContext,
    _config: LoaderConfiguration,
    callbacks: LoaderCallbacks<LoaderContext>
  ): void {
    this.context = context;
    this.stats.loading.start = performance.now();

    this.loadInternal(context, callbacks);
  }

  /**
   * Internal load implementation
   */
  private async loadInternal(
    context: LoaderContext,
    callbacks: LoaderCallbacks<LoaderContext>
  ): Promise<void> {
    try {
      // Create abort controller for this request
      this.abortController = new AbortController();

      const headers: Record<string, string> = {};

      // IMPORTANT: Skip range requests for encrypted segments
      // Range requests (bytes=0-0 probes) return only 1 byte which can't be decrypted with AES-CBC
      // We need full segments for decryption, so we ignore range headers
      const isSegmentFile = this.shouldDecryptUrl(context.url) && !context.url.includes('.m3u8');
      const hasRangeRequest = context.rangeStart !== undefined && context.rangeEnd !== undefined;

      if (hasRangeRequest && !isSegmentFile) {
        // For non-encrypted files (if any), honor range requests
        headers['Range'] = `bytes=${context.rangeStart}-${context.rangeEnd}`;
      }

      const response = await fetch(context.url, {
        signal: this.abortController.signal,
        mode: 'cors', // Explicitly request CORS
        credentials: 'include', // Include cookies for CDN authentication
        headers: Object.keys(headers).length > 0 ? headers : undefined
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get encrypted data
      const inputData = await response.arrayBuffer();

      this.stats.loading.first = performance.now();
      this.stats.total = inputData.byteLength;

      // Determine if this is a video segment that needs decryption
      // All files from the encrypted video path need decryption
      const needsDecryption = this.shouldDecryptUrl(context.url);
      const isTextFile = context.responseType === 'text' || context.url.includes('.m3u8');

      let finalData: ArrayBuffer | string;

      if (needsDecryption) {
        // Decrypt the data (could be playlist or video chunk)
        try {
          const decryptedBuffer = await videoEncryptionService.decryptVideoChunk(
            inputData,
            this.encryptedLoaderConfig.decryptionKeys
          );

          // If it's a text file (playlist), convert to string in order to
          // remove encryption directives
          // This prevents HLS.js from trying to decrypt again
          if (isTextFile) {
            const decoder = new TextDecoder('utf-8');
            let playlistContent = decoder.decode(decryptedBuffer);

            // Remove encryption directives from playlists since we're decrypting ourselves          
            playlistContent = this.removeEncryptionDirectives(playlistContent);

            finalData = playlistContent;
          } else {
            finalData = decryptedBuffer;
          }
        } catch (error) {
          // Log decryption failure
          videoLogger.decryptionEvent(
            this.encryptedLoaderConfig.videoId,
            false,
            context.url,
            error instanceof Error ? error : new Error(String(error))
          );
          throw error; // Re-throw to trigger HLS error handling
        }
      } else {
        // Pass through without decryption
        // Shouldn't happen for CDN videos, but possile for local videos (before upload) and
        // for Chat / Community videos
        if (isTextFile) {
          const decoder = new TextDecoder('utf-8');
          finalData = decoder.decode(inputData);
        } else {
          finalData = inputData;
        }
      }

      this.stats.loaded = typeof finalData === 'string' ? finalData.length : finalData.byteLength;
      this.stats.loading.end = performance.now();

      // Call success callback with decrypted data
      const responseData = {
        url: context.url,
        data: finalData,
        code: response.status,
        text: response.statusText
      };

      callbacks.onSuccess(responseData, this.stats, context, response);

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was aborted, don't call error callback
        return;
      }

      appLogger.error('EncryptedHlsLoader error', 'EncryptedHlsLoader.load', { error: error as Error });

      callbacks.onError(
        {
          code: 0,
          text: error instanceof Error ? error.message : 'Unknown error'
        },
        context,
        null,
        this.stats
      );
    }
  }

  /**
   * Remove encryption directives from playlist
   * Since we decrypt all content ourselves, we need to remove #EXT-X-KEY directives
   * to prevent HLS.js from trying to decrypt again
   */
  private removeEncryptionDirectives(playlistContent: string): string {
    const lines = playlistContent.split('\n');
    const filteredLines = lines.filter(line => {
      // Remove #EXT-X-KEY lines (encryption key directives)
      if (line.trim().startsWith('#EXT-X-KEY:')) {
        return false;
      }
      return true;
    });

    return filteredLines.join('\n');
  }

  /**
   * Determine if URL points to content that should be decrypted
   * ALL video files are encrypted including playlists and segments
   */
  private shouldDecryptUrl(url: string): boolean {
    // Remove query parameters before checking extension
    const urlWithoutQuery = url.split('?')[0].toLowerCase();

    // Decrypt ALL video-related files:
    // - Playlists: .m3u8 (master playlist and variant playlists)
    // - Segments: .m4s (fmp4 segments), .ts (MPEG-TS segments)
    if (urlWithoutQuery.endsWith('.m3u8') ||
      urlWithoutQuery.endsWith('.m4s') ||
      urlWithoutQuery.endsWith('.ts')) {
      return true;
    }

    // Default: don't decrypt (for other resources like keys, subtitles, etc.)
    return false;
  }

  /**
   * Get current loader context
   */
  getContext(): LoaderContext | null {
    return this.context;
  }

  /**
   * Get loader stats
   */
  getStats(): LoaderStats {
    return this.stats;
  }
}

/**
 * Factory function to create encrypted loader
 */
export function createEncryptedLoader(
  videoId: string,
  decryptionKeys: VideoDecryptionKeys
): new (config: HlsConfig) => Loader<LoaderContext> {
  return class extends EncryptedHlsLoader {
    constructor(_config: HlsConfig) {
      super({ videoId, decryptionKeys }, undefined);
    }
  } as any; // Type assertion needed for HLS.js loader compatibility
}
